import http from "../utils/http";
import { GETListResponse } from "./SubscriptionServices";

// ─── Enums (mirror ADMIN_LEADS_API.md) ──────────────────────────────────────

export type LeadStatus = "CREATED" | "LINK_CREATED" | "FULFILLED" | "IGNORED";
export type LeadSource = "LEARN_MORE" | "BANNER";
export type LastInboundAction = "PURCHASE" | "TALK_TO_COACH" | "EXPLORE" | null;
export type LinkDelivery =
  | "SENT_AUTO"
  | "PENDING_MANUAL"
  | "SENT_MANUAL"
  | "FAIL"
  | null;

// ─── DTOs ────────────────────────────────────────────────────────────────────

export interface AdminLeadUser {
  id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
}

export interface AdminLeadLink {
  linkId: string;
  shortUrl: string;
  // Frozen at mint (always "created") — DO NOT use for live/expired. Use linkLive.
  status: string;
  expiresAt: string;
  // The real "is the link payable now?" signal, derived from expiresAt server-side.
  linkLive: boolean;
}

export interface AdminLeadDTO {
  id: string;
  status: LeadStatus;
  source: LeadSource;
  lastInboundAction: LastInboundAction;
  linkDelivery: LinkDelivery;
  programId: string;
  programName: string;
  orderId: string | null; // null until a link is minted
  user: AdminLeadUser | null; // null only if the user record is missing
  link: AdminLeadLink | null; // null when no link is bound yet
  createdAt: string;
  updatedAt: string;
}

export interface PaymentLinkResponse {
  orderId: string;
  linkId: string;
  shortUrl: string;
  status: string; // Razorpay create-time status; not a live/expired signal
  expiresAt: string;
}

export interface AdminLeadActionResponse {
  leadId: string;
  status: LeadStatus;
  linkDelivery: LinkDelivery;
}

// ─── List params ──────────────────────────────────────────────────────────────

export interface LeadListParams {
  page: number;
  size: number;
  status?: LeadStatus;
  lastInboundAction?: Exclude<LastInboundAction, null>;
  hasInboundAction?: boolean; // false = Cold bucket; omit for no filter
  linkDelivery?: Exclude<LinkDelivery, null>;
  needsDelivery?: boolean; // true = PENDING_MANUAL ∪ FAIL; omit for no filter
  search?: string;
  startDate?: string; // yyyy-MM-dd
  endDate?: string; // yyyy-MM-dd
}

// ─── Action result ─────────────────────────────────────────────────────────────
// http.post swallows errors (.catch(err => err)), so a 409/404 resolves with the
// AxiosError instead of throwing. Normalize both shapes into one typed result so
// callers branch on `ok` / `status` rather than try/catch.

export type ActionResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; message?: string };

const normalizeAction = <T>(result: any): ActionResult<T> => {
  // Success: axios response with a 2xx status.
  if (result?.status && result.status >= 200 && result.status < 300) {
    return { ok: true, status: result.status, data: result.data as T };
  }
  // Failure: swallowed AxiosError — real status/body live under `.response`.
  const status = result?.response?.status ?? 0;
  const message =
    result?.response?.data?.message ?? result?.message ?? "Request failed";
  return { ok: false, status, message };
};

// ─── Endpoints ──────────────────────────────────────────────────────────────

/**
 * List leads (worklist). Rows are newest-first (server-side). Combine filters to
 * form buckets — see docs/api-specs/ADMIN_LEADS_API.md.
 */
const getLeads = async (
  params: LeadListParams
): Promise<GETListResponse<AdminLeadDTO>> => {
  const query = new URLSearchParams();
  query.set("page", String(params.page));
  query.set("size", String(params.size));
  if (params.status) query.set("status", params.status);
  if (params.lastInboundAction)
    query.set("lastInboundAction", params.lastInboundAction);
  if (params.hasInboundAction !== undefined)
    query.set("hasInboundAction", String(params.hasInboundAction));
  if (params.linkDelivery) query.set("linkDelivery", params.linkDelivery);
  if (params.needsDelivery !== undefined)
    query.set("needsDelivery", String(params.needsDelivery));
  if (params.search && params.search.trim())
    query.set("search", params.search.trim());
  if (params.startDate) query.set("startDate", params.startDate);
  if (params.endDate) query.set("endDate", params.endDate);

  const result = await http.get({ path: `/admin/leads?${query.toString()}` });
  if (result.status === 200) {
    return result.data as GETListResponse<AdminLeadDTO>;
  }
  throw new Error(`Error fetching leads: ${result.statusText}`);
};

/** Re-send the 3-button interest intro. No state change. 409 if not CREATED. */
const resendIntro = async (
  leadId: string
): Promise<ActionResult<AdminLeadActionResponse>> => {
  const result = await http.post({
    path: `/admin/leads/${leadId}/intro`,
    body: {},
  });
  return normalizeAction<AdminLeadActionResponse>(result);
};

/** IGNORED → CREATED. No message sent. 409 if not IGNORED. */
const reviveLead = async (
  leadId: string
): Promise<ActionResult<AdminLeadActionResponse>> => {
  const result = await http.post({
    path: `/admin/leads/${leadId}/revive`,
    body: {},
  });
  return normalizeAction<AdminLeadActionResponse>(result);
};

/**
 * Create / resend / refresh the payment link. The server decides mint vs.
 * return-existing vs. re-mint from lead state. When `send` is true it also
 * delivers via WhatsApp — but a 200 means the link exists, NOT that delivery
 * succeeded; re-read the lead to see the true linkDelivery. 409 if terminal.
 */
const createPaymentLink = async (
  leadId: string,
  send = true
): Promise<ActionResult<PaymentLinkResponse>> => {
  const result = await http.post({
    path: `/admin/leads/${leadId}/payment-link`,
    body: { send },
  });
  return normalizeAction<PaymentLinkResponse>(result);
};

/** Record that the operator delivered the link themselves → SENT_MANUAL. No body. */
const markLinkDelivered = async (
  leadId: string
): Promise<ActionResult<AdminLeadActionResponse>> => {
  const result = await http.post({
    path: `/admin/leads/${leadId}/link-delivery`,
    body: {},
  });
  return normalizeAction<AdminLeadActionResponse>(result);
};

/** Sweep a non-terminal lead to IGNORED (no-op if already terminal). */
const ignoreLead = async (
  leadId: string
): Promise<ActionResult<AdminLeadActionResponse>> => {
  const result = await http.post({
    path: `/admin/leads/${leadId}/ignore`,
    body: {},
  });
  return normalizeAction<AdminLeadActionResponse>(result);
};

export {
  getLeads,
  resendIntro,
  reviveLead,
  createPaymentLink,
  markLinkDelivered,
  ignoreLead,
};
